<template>
    <div ref="funnelContainer" class="card card-chart">
        <div class="card-header" v-if="$slots.header">
            <slot name="header"/>
        </div>
        <div class="card-body funnel-body p-0 mb-5 ml-3 mr-3">
            <div class="funnel svg-funnel-js w-100 p-15" :class="{'svg-funnel-js--vertical': direction === 'vertical'}">
                <div class="svg-funnel-js__container">
                    <svg width="100%" :height="height">
                        <defs>
                            <linearGradient :id="`funnelGradient-${(index+1)}`"
                                            v-for="(colors, index) in gradientSet"
                                            :key="index"
                                            :gradientTransform="gradientAngle"
                            >
                                <stop :stop-color="color"
                                      :offset="offsetColor(index, colors.values.length)"
                                      v-for="(color, index) in colors.values"
                                      :key="index"
                                />
                            </linearGradient>
                        </defs>
                        <path :fill="colorSet[index].fill" :stroke="colorSet[index].fill"
                              :d="path" v-for="(path, index) in paths" :key="index"
                        />
                    </svg>
                </div>
                <transition-group class="svg-funnel-js__labels" name="appear" tag="div"
                                  v-on:enter="enterTransition" v-on:leave="leaveTransition"
                >
                    <div class="svg-funnel-js__label" :class="`label-${(index+1)}`"
                         v-for="(value, index) in valuesFormatted"
                         :key="labels[index].toLowerCase().split(' ').join('-') + index"
                    >
                        <hr class="m-0 p-0"/>
                        <div class="label__title" v-if="labels">{{ labels[index] | formatLabel($auth.user.customer.settings.dateFormat) }}</div>
                        <div class="label__percentage" v-if="displayPercentage && percentages()[index] !== 100">
                            {{ percentages()[index] }}%
                        </div>
                        <div class="label__segment-percentages funnel-graph-tooltip" v-if="is2d()">
                            <ul class="segment-percentage__list">
                                <li v-for="(subLabel, j) in subLabels" :key="j"
                                    :style="`width: ${100 / subLabels.length}%`">
                                    <p class="percentage__list-label">{{twoDimValues()[index][j] || '-' }}</p>
                                </li>
                            </ul>
                        </div>
                    </div>
                </transition-group>
                <transition name="fade" v-on:enter="enterTransition" v-on:leave="leaveTransition">
                    <div class="svg-funnel-js__subLabels" v-if="is2d()">
                        <div :class="`svg-funnel-js__subLabel svg-funnel-js__subLabel-${(index + 1)}`"
                             v-for="(subLabel, index) in subLabels"
                             :key="index"
                        >
                            <div class="svg-funnel-js__subLabel--color"
                                 :style="subLabelBackgrounds(index)"></div>
                            <div class="svg-funnel-js__subLabel--title">{{ subLabel | languageString }}</div>
                        </div>
                    </div>
                </transition>
            </div>
        </div>
    </div>
</template>

<script>
  import { interpolate } from 'polymorph-js'
  import TWEEN from '@tweenjs/tween.js'
  import FunnelGraph from 'funnel-graph-js'
  import { formatNumber } from 'funnel-graph-js/src/js/number'
  import { getDefaultColors, generateLegendBackground } from 'funnel-graph-js/src/js/graph'
  import 'funnel-graph-js/src/scss/main.scss'
  import 'funnel-graph-js/src/scss/theme.scss'
  import { getBackgroundColor } from '../../../../util/colors'

  export default {
    name: 'VueFunnelGraph',
    props: {
      animated: {
        type: Boolean,
        default: false
      },
      width: [String, Number],
      height: [String, Number],
      values: Array,
      labels: Array,
      subLabels: Array,
      direction: {
        type: String,
        default: 'vertical'
      },
      gradientDirection: {
        type: String,
        default: 'vertical'
      },
      displayPercentage: {
        type: Boolean,
        default: true
      }
    },
    data () {
      return {
        paths: [],
        prevPaths: [], // paths before update, used for animations
        graph: null,
        tween: null,
        defaultColors: getDefaultColors(10),
        window: {
          width: 0
        }
      }
    },
    created () {
      window.addEventListener('resize', this.handleResize)
      this.handleResize()
    },
    mounted () {
      this.setFunnelWidth()
    },
    beforeDestroy () {
      window.removeEventListener('resize', this.setFunnelWidth)
    },
    computed: {
      valuesFormatted () {
        if (this.graph.is2d()) {
          return this.graph.getValues2d().map(value => formatNumber(value))
        }
        return this.values.map(value => formatNumber(value))
      },
      colorSet () {
        const colorSet = []
        let gradientCount = 0
        for (let i = 0; i < this.paths.length; i++) {
          const values = this.graph.is2d() ? this.getColors[i] : this.getColors
          const fillMode = (typeof values === 'string' || values.length === 1) ? 'solid' : 'gradient'
          if (fillMode === 'gradient') gradientCount += 1
          colorSet.push({
            values,
            fillMode,
            fill: fillMode === 'solid' ? values : `url('#funnelGradient-${gradientCount}')`
          })
        }
        return colorSet
      },
      gradientSet () {
        const gradientSet = []
        this.colorSet.forEach((colors) => {
          if (colors.fillMode === 'gradient') {
            gradientSet.push(colors)
          }
        })
        return gradientSet
      },
      getColors () {
        this.colors = []
        for (let i = 0; i < this.values.length; ++i) {
          for (let j = 0; j < this.values[i].length; ++j) {
            if (!this.colors[j]) this.colors[j] = []
            this.colors[j].push(getBackgroundColor(this.values[i][j]))
            if (i === this.values.length - 1) {
              this.colors[j].push(getBackgroundColor(this.values[i][j]))
            }
          }
        }
        return this.colors
      },
      gradientAngle () {
        return `rotate(${this.gradientDirection === 'vertical' ? 90 : 0})`
      }
    },
    methods: {
      setFunnelWidth (event) {
        if (!this.$refs.funnelContainer) return
        const width = this.$refs.funnelContainer.clientWidth * 0.7
        this.graph.setWidth(width)
        this.drawPaths()
        if (this.animated) this.makeAnimations()
      },
      handleResize () {
        this.window.width = window.innerWidth
        this.setFunnelWidth()
      },
      enterTransition (el, done) {
        if (!this.animated) done()
        setTimeout(() => done(), 700)
      },
      leaveTransition (el, done) {
        if (!this.animated) done()
        setTimeout(() => done(), 700)
      },
      is2d () {
        return this.graph.is2d()
      },
      percentages () {
        return this.graph.createPercentages()
      },
      twoDimValues () {
        if (!this.is2d()) {
          return []
        }

        const values = []

        this.graph.values.forEach((valueSet) => {
          values.push(valueSet.map(value => value))
        })
        return values
      },
      subLabelBackgrounds (index) {
        if (!this.is2d()) {
          return []
        }
        return generateLegendBackground(this.getColors[index], this.gradientDirection)
      },
      offsetColor (index, length) {
        return `${Math.round(100 * index / (length - 1))}%`
      },
      makeAnimations () {
        if (this.tween !== null) { this.tween.stop() }
        const interpolators = []
        const dimensionChanged = this.prevPaths.length !== this.paths.length
        let origin = { x: 0.5, y: 0.5 }
        if (dimensionChanged) {
          origin = { x: 0, y: 0.5 }
          if (this.graph.isVertical()) {
            origin = { x: 1, y: 1 }
          }
          if (!this.graph.is2d()) {
            origin = { x: 0, y: 1 }
          }
        }
        this.paths.forEach((path, index) => {
          let oldPath = this.prevPaths[index] || this.graph.getPathMedian(index)
          if (dimensionChanged) oldPath = this.graph.getPathMedian(index)
          const interpolator = interpolate([oldPath, path], {
            addPoints: 1,
            origin,
            optimize: 'fill',
            precision: 1
          })
          interpolators.push(interpolator)
        })

        function animate () {
          if (TWEEN.update()) {
            requestAnimationFrame(animate)
          }
        }

        const position = { value: 0 }
        this.tween = new TWEEN.Tween(position)
          .to({ value: 1 }, 700)
          .easing(TWEEN.Easing.Cubic.InOut)
          .onUpdate(() => {
            for (let index = 0; index < this.paths.length; index++) {
              this.paths[index] = interpolators[index](position.value)
              // eslint-disable-next-line no-underscore-dangle
              this.paths.__ob__.dep.notify()
            }
          })
        this.tween.start()
        animate()
      },
      drawPaths () {
        this.prevPaths = this.paths
        this.paths = []
        const definitions = this.graph.getPathDefinitions()
        definitions.forEach((d) => {
          this.paths.push(d)
        })
      }
    },
    beforeMount () {
      this.graph = new FunnelGraph({
        height: this.height,
        width: this.width,
        container: '.funnel-container',
        direction: this.direction,
        data: {
          labels: this.labels,
          values: this.values
        },
        subLabelValue: 'raw'
      })
      this.drawPaths()
      if (this.animated) this.makeAnimations()
    },
    watch: {
      values () {
        this.graph.setValues(this.values)
        this.drawPaths()
        if (this.animated) this.makeAnimations()
      },
      direction () {
        this.graph.setDirection(this.direction)
          .setWidth(this.width)
          .setHeight(this.height)
        this.drawPaths()
      }
    }
  }
</script>

<style scoped lang="scss">

    .funnel-body {
        // margin-right: 10px !important;
        // margin-left: 10px !important;
    }

    .funnel-graph-tooltip {
        margin-left: 10% !important;
        margin-right: 10% !important;
        @media (max-width: 900px) {
          margin-left: 20% !important;
          margin-right: 0% !important;
        }
        
        width: 80% !important;
        top: 0 !important;
        background-color: rgba(255, 255, 255, 0.8) !important;

        li {
            padding: 10px !important;
            text-align: center;
        }
    }

    .svg-funnel-js.svg-funnel-js--vertical .svg-funnel-js__label {
        border-top: none;
        padding: 0;
        margin: 0;

        hr {
            position: relative;
            top: -50%;
            background-color: #0097AE !important;
        }

        div.label__title {
            position: relative;
            top: -0.75rem;
            color: #0097AE !important;
        }

        &:first-child {
            visibility: hidden;
        }
    }

    .svg-funnel-js .svg-funnel-js__labels .svg-funnel-js__label .label__segment-percentages ul li {
        color: #000 !important;
        padding: 5px;
    }

    .appear-enter-active, .appear-leave-active {
        transition: all .7s ease-in-out;
    }

    .appear-enter-to, .appear-leave {
        max-width: 100%;
        max-height: 100%;
        opacity: 1;
    }

    .appear-enter, .appear-leave-to {
        max-width: 0;
        max-height: 0;
        opacity: 0;
    }

    .fade-enter-active, .fade-leave-active {
        transition: all .3s ease;
    }

    .fade-enter-to, .fade-leave {
        opacity: 1;
    }

    .fade-enter, .fade-leave-to {
        opacity: 0;
    }

    .funnel-container {
    }

    .w-100 {
        width: 100%;
    }

    .p-15 {
        padding-left: 15%;
        padding-right: 0;
    }


    @media (max-width: 900px) {
      .svg-funnel-js__subLabels {
        display: block;
        position: relative;
      }

      .svg-funnel-js .svg-funnel-js__subLabels .svg-funnel-js__subLabel:not(:first-child) {
        margin-left: 0px;
      }

      .percentage__list-label {
        white-space: nowrap;
      }

      .svg-funnel-js .svg-funnel-js__container {
        margin-left: 20px;
      }
    }

</style>
